import { Link } from '@tanstack/react-router';
import type { ColumnDef, SortingState } from '@tanstack/react-table';

import type { MutualFundRow } from '#/api/generated/fINRAMarketDataAPI.schemas';
import type { MutualFundSortColumnId } from '#/components/index/MutualFundsExploreTable/types';
import { Indicator } from '#/components/ui/Indicator';
import { NoData } from '#/components/ui/NoData';
import { Ticker } from '#/components/ui/Ticker';
import { DASH } from '#/utils/constants';
import {
  formatCompactCurrency,
  formatCurrency,
  roundDecimal,
} from '#/utils/format';

export function mutualFundRowId(row: MutualFundRow, index: number) {
  return `mutual-fund-${row.ticker ?? row.name}-${index}`;
}

export function getSortColumn(
  sorting: SortingState,
  fallbackSortColumn: MutualFundSortColumnId,
): MutualFundSortColumnId {
  const columnId = sorting[0]?.id;

  if (
    columnId === 'expense_ratio' ||
    columnId === 'price' ||
    columnId === 'trailing_1yr_return' ||
    columnId === 'return_5Y_annualized' ||
    columnId === 'fund_size'
  ) {
    return columnId;
  }

  return fallbackSortColumn;
}

export function getSortDirection(sorting: SortingState): 'asc' | 'desc' {
  return sorting[0]?.desc === false ? 'asc' : 'desc';
}

const API_SORT_KEY_BY_COLUMN: Record<
  MutualFundSortColumnId,
  'expense_ratio' | 'price' | 'return_1y' | 'return_5y' | 'fund_size'
> = {
  expense_ratio: 'expense_ratio',
  price: 'price',
  trailing_1yr_return: 'return_1y',
  return_5Y_annualized: 'return_5y',
  fund_size: 'fund_size',
};

export function getApiSortKey(sortColumn: MutualFundSortColumnId) {
  return API_SORT_KEY_BY_COLUMN[sortColumn];
}

export type MutualFundColumnId =
  | 'ticker'
  | 'expense_ratio'
  | 'price'
  | 'trailing_1yr_return'
  | 'return_5Y_annualized'
  | 'fund_size';

interface MutualFundColumnOptions {
  compact?: boolean;
  /**
   * Overrides the ticker link's analytics annotation. Defaults to the explore
   * table's `explore_table_ticker` / `mutual_fund_explore_table`; the search
   * page passes `search_result_row` / `search_results` so its rows are
   * attributed distinctly without affecting other tables.
   */
  tickerTrack?: { track: string; context: string };
}

const COMPACT_TICKER_COLUMN = {
  size: 130,
  minSize: 110,
  maxSize: 130,
} as const;

const COMPACT_EXPENSE_RATIO_COLUMN = {
  size: 92,
  minSize: 84,
  maxSize: 92,
} as const;

const COMPACT_PRICE_COLUMN = {
  size: 64,
  minSize: 56,
  maxSize: 64,
} as const;

const COMPACT_RETURN_COLUMN = {
  size: 88,
  minSize: 80,
  maxSize: 88,
} as const;

export function mutualFundColumns(
  firstColumnLabel: string,
  visibleColumns: MutualFundColumnId[] = [
    'ticker',
    'expense_ratio',
    'price',
    'return_5Y_annualized',
    'fund_size',
  ],
  options?: MutualFundColumnOptions,
): ColumnDef<MutualFundRow>[] {
  const compact = options?.compact ?? false;
  const tickerTrack = options?.tickerTrack ?? {
    track: 'explore_table_ticker',
    context: 'mutual_fund_explore_table',
  };
  const tickerSizing = compact
    ? COMPACT_TICKER_COLUMN
    : { size: 300, minSize: 220, maxSize: undefined };

  const columns: Record<MutualFundColumnId, ColumnDef<MutualFundRow>> = {
    ticker: {
      id: 'ticker',
      accessorKey: 'ticker',
      header: compact ? 'Ticker' : firstColumnLabel,
      enableSorting: false,
      size: tickerSizing.size,
      minSize: tickerSizing.minSize,
      maxSize: tickerSizing.maxSize,
      meta: { announceHeader: true },
      cell: ({ row }) => {
        if (!row.original.ticker && !row.original.name) {
          return <NoData />;
        }

        const title = row.original.name ?? DASH;

        if (!row.original.ticker) {
          return (
            <span className="flex min-w-0 items-center gap-8">
              <Ticker
                symbol={row.original.ticker ?? DASH}
                size={compact ? 'sm' : 'md'}
              />
              <span className="text-static-12 min-w-0 truncate">{title}</span>
            </span>
          );
        }

        return (
          <span className="flex min-w-0 items-center gap-8">
            <Ticker
              symbol={row.original.ticker ?? DASH}
              size={compact ? 'sm' : 'md'}
            />
            <Link
              className="text-static-12 min-w-0 truncate"
              to="/$category/$id"
              params={{ category: 'mutual-funds', id: row.original.ticker }}
              data-track={tickerTrack.track}
              data-track-label={row.original.ticker}
              data-track-context={tickerTrack.context}
            >
              {title}
            </Link>
          </span>
        );
      },
    },
    expense_ratio: {
      id: 'expense_ratio',
      header: 'Expense Ratio',
      accessorKey: 'expense_ratio',
      enableSorting: true,
      ...(compact ? COMPACT_EXPENSE_RATIO_COLUMN : {}),
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) => {
        const value = getValue<number | null>() ?? null;

        return value === null ? <NoData /> : `${roundDecimal(value) ?? 0}%`;
      },
    },
    price: {
      id: 'price',
      header: 'Price',
      accessorKey: 'price',
      enableSorting: true,
      ...(compact ? COMPACT_PRICE_COLUMN : {}),
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) => {
        return formatCurrency(getValue<number | null>()) ?? <NoData />;
      },
    },
    trailing_1yr_return: {
      id: 'trailing_1yr_return',
      accessorKey: 'trailing_1yr_return',
      header: '1-Year Return',
      enableSorting: true,
      ...(compact ? COMPACT_RETURN_COLUMN : {}),
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) => {
        const value = getValue<number | null>() ?? null;

        if (value === null) {
          return <NoData />;
        }

        return (
          <Indicator
            className={compact ? 'text-static-12' : 'text-static-14'}
            size={compact ? 'sm' : 'md'}
            value={roundDecimal(value) ?? 0}
            suffix="%"
            type="percent"
          />
        );
      },
    },
    return_5Y_annualized: {
      id: 'return_5Y_annualized',
      accessorKey: 'return_5Y_annualized',
      header: '5-Year Return',
      enableSorting: true,
      ...(compact ? COMPACT_RETURN_COLUMN : {}),
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) => {
        const value = getValue<number | null>() ?? null;

        if (value === null) {
          return <NoData />;
        }

        return (
          <Indicator
            className={compact ? 'text-static-12' : 'text-static-14'}
            size={compact ? 'sm' : 'md'}
            value={roundDecimal(value) ?? 0}
            suffix="%"
            type="percent"
          />
        );
      },
    },
    fund_size: {
      accessorKey: 'fund_size',
      header: 'Fund Size',
      enableSorting: true,
      ...(compact ? COMPACT_RETURN_COLUMN : {}),
      meta: { align: 'end', announceHeader: true },
      cell: ({ getValue }) =>
        formatCompactCurrency(getValue<number | null>()) ?? <NoData />,
    },
  };

  return visibleColumns.map((columnId) => columns[columnId]);
}
